import { action, computed, observable } from 'mobx';
import { SortingRule } from 'react-table';
import debounce from 'debounce';

import httpFacade from 'http/httpFacade';
import { EmailShort } from 'http/Api/Email/types';
import { PageableListRequestParams } from 'http/types';
import { mapSortingRulesToValues } from 'stores/Employees/helpers';
import Log from 'helpers/log';

import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_THROTTLE_MS,
} from '../constants';
import { PromiseService } from 'services/PromiseService';

class EmailsStore {
  @observable emails: EmailShort[] = [];
  @observable sorted: SortingRule[];
  @observable pageCount: number;
  @observable size = DEFAULT_PAGE_SIZE;
  @observable page = DEFAULT_PAGE;
  @observable searchTerm = '';

  @observable private _promiseService = new PromiseService();

  onSearch = debounce<() => void>(this.fetchEmails, DEFAULT_THROTTLE_MS);

  @action.bound
  async fetchEmails(params?: PageableListRequestParams) {
    try {
      const { data } = await this._promiseService.add(() =>
        httpFacade.email.fetchEmailAll({
          ...(this.sorted !== undefined
            ? { sort: mapSortingRulesToValues(this.sorted)[0] }
            : {}),
          ...(this.size !== undefined ? { size: this.size } : {}),
          ...(this.page !== undefined ? { page: this.page } : {}),
          ...(this.searchTerm ? { q: this.searchTerm } : {}),
          ...params,
        }),
      );
      const { content, totalPages } = data;

      this.emails = content;
      this.pageCount = totalPages;
    } catch (error) {
      Log.warn(error);
    }
  }

  @action.bound
  async changeSort(newSorted: SortingRule[]) {
    this.sorted = newSorted;
    await this.fetchEmails();
  }

  @action.bound
  async changeSize(size) {
    this.size = size;
    await this.fetchEmails();
  }

  @action.bound
  async changePageCurrent(page) {
    this.page = page;
    await this.fetchEmails();
  }

  @action.bound
  changeSearch(value: string) {
    this.page = DEFAULT_PAGE;
    this.searchTerm = value;
    this.onSearch();
  }

  @computed
  get loading() {
    return this._promiseService.isPending || !this._promiseService.isCompleted;
  }
}

export default EmailsStore;
